import { CalendarDate, parseZonedDateTime } from '@internationalized/date';
import { maybeParseCalendarDate } from '@rematter/common/dateTime';
import {
  enumSalesLoadLineItemPhase,
  LoadSortOption,
  LoadStatus,
  Weight,
} from '@rematter/frontend-lib-api-client';

import {
  enumLoadWeightType,
  LoadLineItemRecord,
  LoadRecord,
} from '../types/load';
import { toMoney } from '../utils/money';
import { calculateLineItemsTotal } from '../utils/weight';

import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';

export const loadsListOptimizedQueryAtom = createQueryAtomFactory(
  'trade.loadsListOptimized',
  (params: {
    ids?: string[];
    status?: LoadStatus[];
    searchTerm?: string;
    fromExpectedShipDate?: CalendarDate;
    toExpectedShipDate?: CalendarDate;
    facilityId?: string;
    facilityIds?: string[];
    salesOrderIds?: string[];
    accountIds?: string[];
    createdByUserIds?: string[];
    fromColumnDate?: CalendarDate;
    toColumnDate?: CalendarDate;
    columnStatuses?: LoadStatus[];
    bookingId?: string;
    salesOrderId?: string;
    hasSupplier?: boolean;
    limit?: number;
    offset?: number;
    sorting?: LoadSortOption[];
  }) => ({
    loads: {
      __args: {
        filter: {
          ids: params.ids,
          status: params.columnStatuses || params.status,
          searchTerm: params.searchTerm || undefined,
          salesOrderId: params.salesOrderId,
          salesOrderIds: params.salesOrderIds,
          accountIds: params.accountIds,
          createdBy: params.createdByUserIds,
          fromExpectedShipDate: (
            params.fromColumnDate || params.fromExpectedShipDate
          )?.toString(),
          toExpectedShipDate: (
            params.toColumnDate || params.toExpectedShipDate
          )?.toString(),
          facilityId: params.facilityId,
          facilityIds: params.facilityIds,
          bookingIds: params.bookingId ? [params.bookingId] : undefined,
          hasSupplier: params.hasSupplier,
        },
        sorting: params.sorting,
        pagination: {
          limit: params.limit ?? 25,
          offset: params.offset ?? 0,
        },
      },
      page: {
        total: true,
      },
      entries: {
        id: true,
        status: true,
        reviewed: true,
        expectedShipDate: true,
        shippedDate: true,
        documentNumber: true,
        salesOrder: {
          documentNumber: true,
          account: {
            name: true,
          },
        },
        booking: {
          documentNumber: true,
        },
        lineItems: {
          id: true,
          itemId: true,
          item: {
            id: true,
            name: true,
          },
          unitPrice: {
            amount: true,
            currencyCode: true,
          },
          pricingUnit: true,
          phase: true,
          weights: true,
        },
        reconciliation: {
          ticketNumber: true,
        },
        // Only shipping fields used in columns
        facilityId: true,
        shippingCarrierName: true,
        containerNumber: true,
        releaseNumber: true,
        // Created by info
        createdBy: true,
        createdByUser: {
          id: true,
          firstName: true,
          lastName: true,
        },
        timestamps: {
          createdAt: true,
        },
      },
    },
  }),
  {
    select: ({
      loads,
    }): {
      total: number;
      loads: LoadRecord[];
    } => ({
      loads: loads.entries.map((load) => {
        const shippedLineItems = load.lineItems.filter(
          (item) => item.phase === enumSalesLoadLineItemPhase.Shipped,
        );

        const requestedLineItems = load.lineItems.filter(
          (item) =>
            item.phase === enumSalesLoadLineItemPhase.Requested || !item.phase,
        );

        // Requested or Shipped net quantity based on status
        const relevantLineItems = shippedLineItems?.length
          ? shippedLineItems
          : requestedLineItems;

        const netQuantity = relevantLineItems.reduce(
          (acc, lineItem) =>
            acc +
            Number(lineItem.weights?.[enumLoadWeightType.Net]?.value ?? 0),
          0,
        );

        const shippedTotal = calculateLineItemsTotal(
          shippedLineItems as unknown as Partial<LoadLineItemRecord>[],
        );

        return {
          id: load.id,
          loadNumber: load.documentNumber.toString(),
          status: load.status as LoadStatus,
          reviewed: load.reviewed ?? false,
          expectedShipDate: maybeParseCalendarDate(load.expectedShipDate),
          shippedDate: maybeParseCalendarDate(load.shippedDate),
          loadReceivedDate: undefined,
          salesOrderId: undefined,
          salesOrderNumber: load.salesOrder?.documentNumber ?? undefined,
          salesOrder: undefined, // Not needed for list view
          bookingId: undefined,
          bookingNumber: load.booking?.documentNumber ?? undefined,
          facilityId: load.facilityId ?? undefined,
          shippingCarrierName: load.shippingCarrierName ?? undefined,
          scac: undefined,
          freightForwarder: undefined,
          truckFreight: undefined,
          deliveryNumber: undefined,
          releaseNumber: load.releaseNumber ?? undefined,
          truckNumber: undefined,
          trailerNumber: undefined,
          containerNumber: load.containerNumber ?? undefined,
          sealNumber: undefined,
          containerTypeId: undefined,
          containerType: undefined,
          internalNotes: undefined,
          externalNotes: undefined,
          invoiceId: undefined,
          accountId: undefined,
          customer: load.salesOrder?.account?.name ?? undefined,
          receivingTicketNumber: load.reconciliation?.ticketNumber ?? undefined,
          shippedTotal,
          files: undefined, // Not needed for list view
          lineItems: load.lineItems.map((lineItem) => ({
            id: lineItem.id,
            itemId: lineItem.itemId ?? null,
            type: 'Raw' as const,
            loadId: null,
            item: lineItem.item ?? null,
            tagId: '',
            unitPrice: lineItem.unitPrice ? toMoney(lineItem.unitPrice) : null,
            estimatedPrice: null,
            pricingUnit: lineItem.pricingUnit,
            phase: lineItem.phase,
            weights: lineItem.weights as Record<string, Weight>,
          })),
          lineItemCount: load.lineItems.length,
          netRequestedQuantity: requestedLineItems.reduce(
            (acc, lineItem) =>
              acc +
              Number(lineItem.weights?.[enumLoadWeightType.Net]?.value ?? 0),
            0,
          ),
          netQuantity,
          createdBy: load.createdBy ?? undefined,
          createdByUser: load.createdByUser
            ? {
                id: load.createdByUser.id,
                firstName: load.createdByUser.firstName,
                lastName: load.createdByUser.lastName,
              }
            : null,
          timestamps: {
            createdAt: parseZonedDateTime(load.timestamps.createdAt),
          },
        };
      }),
      total: loads.page.total ?? 0,
    }),
  },
);
